import React, {
  createContext,
  useContext,
  useMemo,
  type CSSProperties,
  type PropsWithChildren,
} from 'react';
import { useSortable } from '@dnd-kit/sortable';
import type { UniqueIdentifier } from '@dnd-kit/core';
import type { DraggableSyntheticListeners, DraggableAttributes } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicatorRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import { dropLineAboveStyle, dropLineBelowStyle } from './style';

interface SortableItemContextValue {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners | undefined;
  ref: (node: HTMLElement | null) => void;
}

const SortableItemContext = createContext<SortableItemContextValue>({
  attributes: {} as DraggableAttributes,
  listeners: undefined,
  ref: () => {},
});

interface DraggableListItemProps {
  id: UniqueIdentifier;
  isPreview?: boolean;
  isOver?: boolean;
  overIndex?: number;
  itemIndex?: number;
  showDropLine?: boolean;
}

export function DraggableListItem({
  children,
  id,
  isPreview = false,
  isOver = false,
  overIndex,
  itemIndex,
  showDropLine = false
}: PropsWithChildren<DraggableListItemProps>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const contextValue = useMemo(
    () => ({ attributes, listeners, ref: setActivatorNodeRef }),
    [attributes, listeners, setActivatorNodeRef]
  );

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging && !isPreview ? 0.4 : 1,
    boxShadow: isPreview ? '0 2px 12px rgba(0,0,0,0.15)' : undefined,
    background: isPreview ? 'white' : undefined,
    borderRadius: isPreview ? 8 : undefined,
    zIndex: isPreview ? 9999 : undefined,
    width: isPreview ? 300 : '100%',
    height: isDragging && !isPreview ? '56px' : undefined,
    minHeight: isDragging && !isPreview ? '56px' : undefined,
    overflow: isDragging && !isPreview ? 'hidden' : undefined,
    pointerEvents: isDragging && !isPreview ? 'none' : undefined,
  };

  // Calculate whether to show drop line above or below
  const shouldShowLineAbove = isOver && itemIndex !== undefined && overIndex !== undefined && overIndex < itemIndex;
  const shouldShowLineBelow = isOver && itemIndex !== undefined && overIndex !== undefined && overIndex >= itemIndex;

  return (
    <SortableItemContext.Provider value={contextValue}>
       <>
        {showDropLine && shouldShowLineAbove && (
          <Box style={dropLineAboveStyle} />
        )}
        <li ref={setNodeRef} style={style}>
          {children}
        </li>
        {showDropLine && shouldShowLineBelow && (
          <Box style={dropLineBelowStyle} />
        )}
      </>
    </SortableItemContext.Provider>
  );
}

export function DragHandle({ className }: { className?: string }) {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <button
      {...attributes}
      {...listeners}
      ref={ref}
      className={className}
      style={{ cursor: 'grab', background: 'transparent', border: 'none', padding: 0 }}
    >
      <DragIndicatorRounded sx={{ color: 'primary.400' }} />
    </button>
  );
}
